// pos_local catalog (/v1/catalog/*) → front-un gözlədiyi cloud stok/qrup shape-i.
//
// ID konvensiyası: pos_local id-ləri cloud id-dən törəyib (projector "prd_{id}",
// "cat_{id}"), ona görə geri çevirmək sadədir və cloud detal endpoint-ləri ilə
// (məs. /stocks/:id cloud-a düşsə belə) id-lər uyğun qalır.

export interface PosProduct {
  id: string; // "prd_42"
  sku?: string | null;
  name: string;
  categoryId?: string | null; // "cat_7"
  priceCents: number;
  taxRateBp: number;
  /** Cloud R2-dəki şəklin tam URL-i — catalog stream-i ilə sync olunur. */
  imageUrl?: string | null;
  isActive: boolean;
  /** Yalnız tək məhsul endpoint-i doldurur; siyahıda olmur. */
  modifierGroups?: PosModifierGroup[];
}

export interface PosCategory {
  id: string; // "cat_7"
  name: string;
  sortOrder: number;
  isActive: boolean;
}

/** "prd_42" → 42, "cat_7" → 7. Prefiks yoxdursa Number(raw). */
export function stripPosId(posId: string): number {
  return Number(String(posId).replace(/^[a-z]+_/i, ''));
}

/**
 * Cloud adları çox-dilli JSON ola bilər: `{"az":"Adana kabab","tr":"..."}`.
 * pos_local onları olduğu kimi saxlayır. Burada oxunaqlı ada çeviririk (az → tr → en → ilk).
 */
export function parseI18nName(raw: unknown): string {
  if (typeof raw !== 'string') return raw == null ? '' : String(raw);
  const s = raw.trim();
  if (s.startsWith('{') && s.endsWith('}')) {
    try {
      const obj = JSON.parse(s) as Record<string, unknown>;
      if (obj && typeof obj === 'object') {
        const v = obj.az ?? obj.tr ?? obj.en ?? Object.values(obj)[0];
        if (typeof v === 'string' && v) return v;
      }
    } catch {
      /* düz mətndir */
    }
  }
  return raw;
}

/** pos məhsulu → cloud stok obyekti (front stocks/stocks-simple gözləntisi). */
export function posProductToCloud(p: PosProduct): Record<string, unknown> {
  return {
    id: stripPosId(p.id),
    name: parseI18nName(p.name),
    price: p.priceCents / 100,
    stock_group_id: p.categoryId ? stripPosId(p.categoryId) : null,
    in_menu: p.isActive ? 1 : 0,
    is_active: p.isActive,
    amount: null, // inventar (stok qalığı) pos_local catalog-da yoxdur
    tax_rate: p.taxRateBp / 100,
    // Şəkil: pos_local TAM URL saxlayır (R2). Front `resolveImageUrl()` tam URL-i
    // olduğu kimi qaytarır, ona görə həm `image`, həm `image_url` sahələrini veririk
    // (komponentlər ikisindən birini oxuyur).
    image: p.imageUrl ?? null,
    image_url: p.imageUrl ?? null,
    // Qrup yoxdursa sahə ÜMUMİYYƏTLƏ əlavə olunmur — köhnə ekranlar
    // modifierGroups görmədikləri üçün davranışları dəyişmir.
    ...(Array.isArray(p.modifierGroups) && p.modifierGroups.length > 0
      ? { modifierGroups: p.modifierGroups.map(posModifierGroupToCloud) }
      : {}),
    _posId: p.id,
  };
}

/** pos kateqoriyası → cloud stok-qrup obyekti. */
export function posCategoryToCloud(c: PosCategory): Record<string, unknown> {
  return {
    id: stripPosId(c.id),
    name: parseI18nName(c.name),
    sort_order: c.sortOrder,
    is_active: c.isActive,
    _posId: c.id,
  };
}

// ─── Opsiya qrupları (ölçü, əlavə tərkib) ────────────────────────────────────
//
// pos_local `GET /v1/catalog/products/:id` cavabında `modifierGroups` qaytarır.
// Cloud-da belə bir sahə YOXDUR — ona görə front bunu opsionel oxumalıdır:
// qrup yoxdursa ekran əvvəlki kimi işləyir (məhsul birbaşa səbətə düşür).
//
// ID-LƏR ÇEVRİLMİR. Məhsul/kateqoriya id-ləri cloud-dan törədiyi üçün ("prd_42")
// prefiksi soyuruq, opsiyalar isə yalnız pos_local daxilində mənalıdır və eyni
// sətirlə `POST /v1/orders/:id/items { modifierIds }` içinə geri gedir.
// Rəqəmə çevirsək geri qaytara bilməzdik.

export interface PosModifier {
  id: string; // "mod_12" — OLDUĞU KİMİ saxlanılır
  name: string;
  priceDeltaCents: number; // MƏNFİ ola bilər ("Sossuz" −0,50)
  isActive: boolean;
  groupId?: string | null;
  isDefault?: boolean;
  sortOrder?: number;
}

export interface PosModifierGroup {
  id: string;
  productId: string;
  name: string;
  /** 0 = opsionel. Heç bir qrup məcburi olmaq məcburiyyətində deyil. */
  minSelect: number;
  /** null = limitsiz. */
  maxSelect: number | null;
  sortOrder: number;
  isActive: boolean;
  options: PosModifier[];
}

/** Front-un modalda gözlədiyi sadələşdirilmiş qrup (qiymətlər manatla). */
export interface CloudModifierOption {
  id: string;
  name: string;
  priceDelta: number;
  isDefault: boolean;
}

export interface CloudModifierGroup {
  id: string;
  name: string;
  minSelect: number;
  maxSelect: number | null;
  options: CloudModifierOption[];
}

export function posModifierGroupToCloud(g: PosModifierGroup): CloudModifierGroup {
  return {
    id: g.id,
    name: parseI18nName(g.name),
    minSelect: Number(g.minSelect ?? 0),
    maxSelect: g.maxSelect === null || g.maxSelect === undefined ? null : Number(g.maxSelect),
    options: (g.options ?? [])
      .filter((o) => o.isActive !== false)
      .map((o) => ({
        id: o.id,
        name: parseI18nName(o.name),
        priceDelta: Number(o.priceDeltaCents ?? 0) / 100,
        isDefault: o.isDefault === true,
      })),
  };
}
